import { useEffect, useRef } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { ClientType, PartnershipType } from '../../types';
import { InsurerType, ToolFormValues } from './tool-validation-schema';

/**
 * Query params that seed the form, so a record page can hand the tool the
 * client insurer an operator is already looking at.
 */
export const TOOL_PREFILL_CLIENT_ID_PARAM = 'clientId';
export const TOOL_PREFILL_INSURER_ID_PARAM = 'insurerId';
export const TOOL_PREFILL_PARTNERSHIP_ID_PARAM = 'partnershipId';

export interface UseToolUrlPrefillParams {
  clientOptions: ClientType[];
  insurerOptions: InsurerType[];
  partnershipOptions: PartnershipType[];
  setValue: UseFormSetValue<ToolFormValues>;
}

const readIdParam = (
  searchParams: URLSearchParams,
  key: string,
): number | undefined => {
  const raw = searchParams.get(key);

  if (raw === null) {
    return undefined;
  }

  const parsed = Number(raw);

  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

/**
 * Seeds client, insurer, and partnership from the URL once the option lists
 * have loaded.
 *
 * The three fields are set in a single pass rather than one at a time:
 * `useBillingToolData` narrows the client and insurer options off whichever of
 * them is already selected, so setting them one by one would filter the
 * options out from under the fields that hadn't been set yet.
 *
 * Runs once. After that the form belongs to the operator — re-applying on
 * every options change would fight their edits.
 */
export const useToolUrlPrefill = ({
  clientOptions,
  insurerOptions,
  partnershipOptions,
  setValue,
}: UseToolUrlPrefillParams): void => {
  const [searchParams] = useSearchParams();
  const hasPrefilled = useRef(false);

  const clientId = readIdParam(searchParams, TOOL_PREFILL_CLIENT_ID_PARAM);
  const insurerId = readIdParam(searchParams, TOOL_PREFILL_INSURER_ID_PARAM);
  const partnershipId = readIdParam(
    searchParams,
    TOOL_PREFILL_PARTNERSHIP_ID_PARAM,
  );

  useEffect(() => {
    if (hasPrefilled.current) {
      return;
    }

    if (!clientId && !insurerId && !partnershipId) {
      return;
    }

    const client = clientId
      ? clientOptions.find(option => option.id === clientId)
      : undefined;
    const insurer = insurerId
      ? insurerOptions.find(option => option.id === insurerId)
      : undefined;
    const partnership = partnershipId
      ? partnershipOptions.find(option => option.id === partnershipId)
      : undefined;

    // Every id in the URL has to resolve before anything is applied. Options
    // arrive from separate queries, so a partial match here means the lists
    // are still filling in rather than the record being genuinely absent.
    const isStillResolving =
      (clientId && !client) ||
      (insurerId && !insurer) ||
      (partnershipId && !partnership);

    if (isStillResolving) {
      return;
    }

    hasPrefilled.current = true;

    const setOptions = { shouldDirty: true, shouldValidate: true };

    if (client) {
      setValue('client', client, setOptions);
    }

    if (insurer) {
      setValue('insurer', insurer, setOptions);
    }

    if (partnership) {
      setValue('partnership', partnership, setOptions);
    }
  }, [
    clientId,
    insurerId,
    partnershipId,
    clientOptions,
    insurerOptions,
    partnershipOptions,
    setValue,
  ]);
};
